'use client';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { Copy, Facebook, MessageCircle, Share2, Twitter } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import {
    FacebookMessengerShareButton,
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
} from 'react-share';
import { toast } from 'sonner';

interface ShareMenuProps {
    url: string;
    title: string;
    description?: string;
    image?: string;
}

interface ShareOptionsProps {
    url: string;
    title: string;
    description?: string;
    image?: string;
    onCopy: () => void;
}

function ShareOptions({
    url,
    title,
    description,
    image,
    onCopy,
}: ShareOptionsProps) {
    const shareProps = {
        url,
        title,
        description: description || title,
        ...(image && { image }),
    };

    return (
        <>
            <FacebookShareButton className="w-full" {...shareProps}>
                <DropdownMenuItem
                    className="w-full"
                    onSelect={(e) => e.preventDefault()}
                >
                    <Facebook className="mr-2 h-4 w-4" />
                    Facebook
                </DropdownMenuItem>
            </FacebookShareButton>

            <FacebookMessengerShareButton
                className="w-full"
                {...shareProps}
                appId={import.meta.env.VITE_FACEBOOK_APP_ID || ''}
            >
                <DropdownMenuItem
                    className="w-full"
                    onSelect={(e) => e.preventDefault()}
                >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Messenger
                </DropdownMenuItem>
            </FacebookMessengerShareButton>

            <TwitterShareButton className="w-full" {...shareProps}>
                <DropdownMenuItem
                    className="w-full"
                    onSelect={(e) => e.preventDefault()}
                >
                    <Twitter className="mr-2 h-4 w-4" />
                    Twitter
                </DropdownMenuItem>
            </TwitterShareButton>

            <WhatsappShareButton className="w-full" {...shareProps}>
                <DropdownMenuItem
                    className="w-full"
                    onSelect={(e) => e.preventDefault()}
                >
                    <FaWhatsapp className="mr-2 h-4 w-4" />
                    WhatsApp
                </DropdownMenuItem>
            </WhatsappShareButton>

            <DropdownMenuSeparator />

            <DropdownMenuItem className="w-full" onClick={onCopy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy Link
            </DropdownMenuItem>
        </>
    );
}

function ShareSheetContent({
    url,
    title,
    description,
    image,
    onCopy,
}: ShareOptionsProps) {
    const shareProps = {
        url,
        title,
        description: description || title,
        ...(image && { image }),
    };

    return (
        <div className="flex flex-col gap-2 p-4">
            <div className="flex gap-2 *:flex-1">
                <FacebookShareButton {...shareProps}>
                    <Button
                        variant="outline"
                        type="button"
                        size="icon"
                        className="w-full"
                    >
                        <Facebook />
                        <span className="sr-only">Share on Facebook</span>
                    </Button>
                </FacebookShareButton>
                <FacebookMessengerShareButton
                    {...shareProps}
                    appId={import.meta.env.VITE_FACEBOOK_APP_ID || ''}
                >
                    <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        className="w-full"
                    >
                        <MessageCircle />
                        <span className="sr-only">Share on Messenger</span>
                    </Button>
                </FacebookMessengerShareButton>

                <TwitterShareButton {...shareProps}>
                    <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        className="w-full"
                    >
                        <Twitter />
                        <span className="sr-only">Share on Twitter</span>
                    </Button>
                </TwitterShareButton>

                <WhatsappShareButton {...shareProps}>
                    <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        className="w-full"
                    >
                        <FaWhatsapp />
                        <span className="sr-only">Share on WhatsApp</span>
                    </Button>
                </WhatsappShareButton>
            </div>
            <div className="my-2 border-t" />
            <Button
                variant="outline"
                className="justify-start"
                onClick={onCopy}
                type="button"
            >
                <Copy className="mr-2 h-4 w-4" />
                Copy Link
            </Button>
        </div>
    );
}

export function ShareMenu({ url, title, description, image }: ShareMenuProps) {
    const isMobile = useIsMobile();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copied to clipboard!');
        } catch (error) {
            toast.error('Failed to copy link');
        }
    };

    const triggerButton = (
        <Button
            size="icon"
            className="h-full"
            variant="outline"
            aria-label="Share"
        >
            <Share2 className="h-5 w-5" />
        </Button>
    );

    if (isMobile) {
        return (
            <Sheet>
                <SheetTrigger asChild>{triggerButton}</SheetTrigger>
                <SheetContent side="bottom" className="h-auto">
                    <SheetHeader>
                        <SheetTitle>Share - {title}</SheetTitle>
                    </SheetHeader>
                    <ShareSheetContent
                        url={url}
                        title={title}
                        description={description}
                        image={image}
                        onCopy={handleCopy}
                    />
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                <ShareOptions
                    url={url}
                    title={title}
                    description={description}
                    image={image}
                    onCopy={handleCopy}
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
